import { useState } from 'react';

interface Rule {
  id: string;
  category: string;
  desc: string;
  enabled: boolean;
  lastModified: string;
  stats: string;
  techTag: string;
  title: string;
}

const PLACEHOLDER_RULES: Rule[] = [
  {
    id: 'email-no-contacto',
    category: 'Comunicaciones y Plantillas',
    title: 'Email por Subproyecto (No Contacto)',
    techTag: 'SUBPROJECT_EMAIL_CONFIG',
    desc: 'Maneja el envío de correos automatizados para clientes que no pudieron ser contactados por el agente.',
    stats: '7/7 activas',
    lastModified: 'hace 2h por Ana Gómez',
    enabled: true,
  },
  {
    id: 'score-threshold',
    category: 'Funnel, Alertas y Scoring',
    title: 'Umbral de Score para Priorización',
    techTag: 'SCORE_THRESHOLD_FOR_CHANGE_STAGE',
    desc: 'Score mínimo (0-100) que debe tener un cliente para pasar automáticamente al estado "Prioritario".',
    stats: '5/5 activas',
    lastModified: 'ayer por Carlos Ruiz',
    enabled: true,
  },
];

const categories = [...new Set(PLACEHOLDER_RULES.map((rule) => rule.category))];

const MasterRulesSection = () => {
  const [ruleStates, setRuleStates] = useState<Record<string, boolean>>(
    Object.fromEntries(PLACEHOLDER_RULES.map((rule) => [rule.id, rule.enabled]))
  );
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRules = PLACEHOLDER_RULES.filter(
    (rule) =>
      !searchQuery ||
      rule.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rule.techTag.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const rulesByCategory = categories.reduce<Record<string, Rule[]>>((acc, category) => {
    acc[category] = filteredRules.filter((rule) => rule.category === category);
    return acc;
  }, {});

  return (
    <section className="space-y-4">
      <header>
        <h2 className="font-display text-lg font-semibold">Rules del master</h2>
        <p className="text-sm text-text-muted">Reglas operativas asociadas al contexto de la inmobiliaria.</p>
      </header>

      <div className="flex items-center gap-3">
        <input
          type="search"
          placeholder="Buscar por nombre o ID técnico..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="flex-1 max-w-xs text-sm bg-bg-surface-raised border border-border-subtle rounded-lg px-3 py-2 outline-none focus:border-brand-vivid transition-colors"
        />
        <button
          type="button"
          onClick={() => {
            // eslint-disable-next-line no-alert
            window.alert('Crear nueva regla - conectar con API');
          }}
          className="text-sm px-3 py-1.5 bg-brand-navy text-text-inverse rounded-lg hover:bg-action-brand-active transition-colors cursor-pointer"
        >
          + Crear regla
        </button>
      </div>

      {categories.map((category) => {
        const rules = rulesByCategory[category];
        if (!rules || rules.length === 0) return null;
        const isCollapsed = collapsedCategories[category];

        return (
          <div key={category} className="border border-border-subtle rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setCollapsedCategories((prev) => ({ ...prev, [category]: !prev[category] }))}
              className="w-full flex items-center justify-between px-4 py-3 bg-bg-surface-raised border-b border-border-subtle text-left"
            >
              <span className="text-sm font-medium text-text-default">{category}</span>
              <span
                className={`text-text-subtle text-sm transition-transform ${isCollapsed ? '' : 'rotate-180'} inline-block`}
                aria-hidden="true"
              >
                ▾
              </span>
            </button>

            {!isCollapsed && (
              <div>
                {rules.map((rule) => (
                  <div
                    key={rule.id}
                    className="flex items-center justify-between px-4 py-3 border-b border-border-subtle last:border-b-0 hover:bg-bg-surface-raised transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-text-default">{rule.title}</span>
                        <code className="text-xs bg-surface-info text-brand-vivid px-1.5 py-0.5 rounded font-mono border border-border-info">
                          {rule.techTag}
                        </code>
                      </div>
                      <p className="text-xs text-text-muted leading-relaxed mb-1">{rule.desc}</p>
                      <p className="text-xs text-text-subtle">Modificado {rule.lastModified}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-text-subtle tabular-nums">{rule.stats}</span>
                      <button
                        type="button"
                        className="text-xs px-2.5 py-1 border border-border-default rounded-lg text-text-default hover:bg-bg-surface-raised transition-colors cursor-pointer"
                      >
                        Configurar
                      </button>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={ruleStates[rule.id]}
                        aria-label={`Activar o desactivar: ${rule.title}`}
                        onClick={() => setRuleStates((prev) => ({ ...prev, [rule.id]: !prev[rule.id] }))}
                        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                          ruleStates[rule.id] ? 'bg-brand-vivid' : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                            ruleStates[rule.id] ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {filteredRules.length === 0 && (
        <p className="text-sm text-text-muted text-center py-6">{`No se encontraron reglas para "${searchQuery}".`}</p>
      )}
    </section>
  );
};

export default MasterRulesSection;
